export class ApiError extends Error {
	constructor(
		message: string,
		public readonly status: number,
	) {
		super(message);
		this.name = 'ApiError';
	}
}

interface StaffSession {
	email: string;
	csrfToken: string;
}

// Browser-tab state only; this module is not used by the Worker request handler.
let staffSession: StaffSession | undefined;
let knownTokens = new Set<string>();
let refresh: Promise<StaffSession> | undefined;
const bootstrapPath = '/api/staff/bootstrap';

function rememberSession(value: unknown): StaffSession {
	if (
		!value ||
		typeof value !== 'object' ||
		!('email' in value) ||
		typeof value.email !== 'string' ||
		!value.email ||
		!('csrfToken' in value) ||
		typeof value.csrfToken !== 'string' ||
		!value.csrfToken
	)
		throw new ApiError('Please refresh the page and sign in again before trying this action.', 401);
	const session = { email: value.email.toLowerCase(), csrfToken: value.csrfToken };
	if (staffSession?.email !== session.email) knownTokens = new Set();
	knownTokens.add(session.csrfToken);
	staffSession = session;
	return session;
}

async function responseData(response: Response): Promise<unknown> {
	if (!response.ok) {
		let message = `Request failed (${response.status})`;
		try {
			const payload = (await response.json()) as { error?: string };
			if (payload.error) message = payload.error;
		} catch {
			// Keep the status-only error for non-JSON responses.
		}
		throw new ApiError(message, response.status);
	}
	return response.json();
}

async function refreshStaffSession(): Promise<StaffSession> {
	if (!refresh) {
		refresh = (async () => {
			const response = await fetch(bootstrapPath, { credentials: 'same-origin', cache: 'no-store' });
			if (response.redirected || !response.headers.get('Content-Type')?.includes('application/json')) {
				throw new ApiError('Please refresh the page and sign in again before trying this action.', 401);
			}
			return rememberSession(await responseData(response));
		})().finally(() => {
			refresh = undefined;
		});
	}
	return refresh;
}

export async function api<T>(path: string, options: RequestInit = {}, csrfToken?: string): Promise<T> {
	const headers = new Headers(options.headers);
	if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
	if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
	const staffWrite = path.startsWith('/api/staff/') && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(options.method?.toUpperCase() ?? 'GET');
	const owner = staffWrite && csrfToken && knownTokens.has(csrfToken) ? staffSession?.email : undefined;
	if (owner && staffSession) headers.set('X-CSRF-Token', staffSession.csrfToken);
	const send = () => fetch(path, { ...options, headers, credentials: 'same-origin' });
	let response = await send();
	if (
		owner &&
		response.status === 403 &&
		response.headers.get('X-BZ-Session-Refresh') === 'required' &&
		(options.body === undefined || typeof options.body === 'string')
	) {
		// A server-declared pre-write rejection is safe to retry once. Never replay
		// network failures, generic authorization failures or uncertain deliveries.
		const session = staffSession && staffSession.csrfToken !== headers.get('X-CSRF-Token') ? staffSession : await refreshStaffSession();
		if (session.email !== owner) {
			throw new ApiError('Your signed-in account changed. Refresh the page before trying this action.', 403);
		}
		headers.set('X-CSRF-Token', session.csrfToken);
		response = await send();
	}
	const data = await responseData(response);
	if (path === bootstrapPath && (options.method ?? 'GET').toUpperCase() === 'GET') rememberSession(data);
	return data as T;
}
